import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { convertDate, formatDescription, formatDuration } from "../Board";
import { Link } from "react-router-dom";
import TaskList from "../components/TaskList";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Modal from "@mui/material/Modal";
import SlaForm from "../components/SlaForm";
import { AuthContext } from "../../../context/authContext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

export default memo(function MyTasks({ showReporterModal = () => {} }) {
  const { currentUser } = useContext(AuthContext);
  const ref = useRef(null);

  const canEditSla = useMemo(() => {
    return (currentUser?.permissions ?? []).includes("sla_write");
  }, [currentUser]);

  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const handleClose = () => {
    setOpen(false);
  };

  const editSla = useCallback((data) => {
    setEditRow(data);
    setOpen(true);
  }, []);

  const reload = useCallback(() => {
    ref.current.reload();
  }, []);

  const tasksHeaders = useMemo(() => {
    return [
      ...(canEditSla
        ? [
            {
              field: "actions",
              type: "actions",
              width: 40,
              getActions: (params) => [
                <GridActionsCellItem
                  icon={<EditRoundedIcon />}
                  label="Edit Sla"
                  onClick={() => editSla(params.row)}
                  showInMenu
                />,
              ],
            },
          ]
        : []),
      ...[
        {
          field: "date",
          headerName: "Date create",
          sortable: false,
          type: "string",
          maxWidth: 150,
          flex: 1,
          valueGetter: (params) => convertDate(params.row.asana_created_at),
        },
        {
          field: "name",
          headerName: "Number",
          sortable: false,
          flex: 1,
          type: "string",
          renderCell: (item) => (
            <>
              <Link to={item.row?.permalink_url} target="_blank">
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatDescription(item.value),
                  }}
                />
              </Link>
            </>
          ),
        },
        {
          field: "notes",
          headerName: "Description",
          sortable: false,
          flex: 1,
          type: "string",
          renderCell: (item) => (
            <span
              dangerouslySetInnerHTML={{
                __html: formatDescription(item.value),
              }}
            />
          ),
        },
        {
          field: "status",
          headerName: "Status",
          sortable: false,
          maxWidth: 100,
          flex: 1,
        },

        {
          field: "reporter",
          headerName: "Reporter",
          sortable: false,
          maxWidth: 200,
          flex: 1,
          renderCell: (item) => (
            <div>
              <div className="flex">
                <span
                  onClick={() => showReporterModal(item.value)}
                  style={{ paddingRight: "3px", cursor: "pointer" }}
                  title={"Show all tasks"}
                >
                  <AccountCircleIcon
                    sx={{ width: "20px", "&:hover": { color: "#1976d2" } }}
                  />
                </span>
                <span style={{ maxWidth: "70px", overflow: "hidden" }}>
                  {item.value}
                </span>
              </div>
              <div>
                <a
                  target={"_blank"}
                  href={`https://app.asana.com/0/search?sort=last_modified&searched_type=task&completion=incomplete&deleted=no_trashed&locatedIn=inAnyOfTheseProjects&any_projects.ids=1205311495757615&custom_field_1205641416680614.variant=contains&custom_field_1205641416680614.value=${item.value}`}
                  rel="noreferrer"
                  style={{ color: "#1976d2" }}
                >
                  Show open tasks
                </a>
              </div>
            </div>
          ),
        },
        {
          field: "sla_first",
          headerName: "SLA first",
          sortable: false,
          type: "string",
          maxWidth: 150,
          flex: 1,
          renderCell: (params) =>
            formatDuration(params.row.sla?.first_comment_seconds ?? 0),
        },
        {
          field: "sla_in_progress",
          headerName: "SLA in progress",
          sortable: false,
          type: "string",
          maxWidth: 150,
          flex: 1,
          renderCell: (params) =>
            formatDuration(params.row.sla?.in_progress_seconds ?? 0),
        },
        {
          field: "sla_complete",
          headerName: "SLA complete",
          sortable: false,
          type: "string",
          maxWidth: 150,
          flex: 1,
          renderCell: (params) =>
            formatDuration(params.row.sla?.lifetime_seconds ?? 0),
        },
      ],
    ];
  }, []);

  return (
    <>
      <TaskList
        ref={ref}
        title="My tasks"
        tasksHeaders={tasksHeaders}
        checkMembers={true}
        filter={{ is_completed: false }}
      />

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: "80vw" }}>
          {open && <SlaForm data={editRow} onSave={reload} />}
        </div>
      </Modal>
    </>
  );
});
